import { defineStore } from 'pinia'
import { useLocationStore } from '~/stores/LocationStore'
import { querySite } from '~/graphql/queries'
import type {
  Site,
  SiteLocale,
  MenuCtaRecord,
  MenuItemRecord,
  FooterLinkRecord,
  LocationCategoryRecord,
  CountryRecord,
  SocialLinkRecord,
  UsefulLinkRecord,
  CategoryRecord
} from '~/types/datocms'

export interface AppState {
  _site: Site
  allMenuCtas: MenuCtaRecord[]
  allMenuItems: MenuItemRecord[]
  allFooterLinks: FooterLinkRecord[]
  allSocialLinks: SocialLinkRecord[]
  allUsefulLinks: UsefulLinkRecord[]
  isLoading: boolean
  theme: 'light' | 'dark'
  projectsCount: number
  locationsCount: number
  simpleFooter: boolean
  showFooter: boolean
  gradientHeader: 'cream' | 'black' | 'grey' | 'yellow'
}

export const useAppStore = defineStore('AppStore', {
  state: (): AppState => {
    return {
      _site: {} as Site,
      allMenuCtas: [],
      allMenuItems: [],
      allFooterLinks: [],
      allSocialLinks: [],
      allUsefulLinks: [],
      isLoading: true,
      theme: 'light',
      projectsCount: 0,
      locationsCount: 0,
      simpleFooter: false,
      showFooter: true,
      gradientHeader: 'black'
    }
  },
  actions: {
    async fetchData(locale: SiteLocale) {
      const locationStore = useLocationStore()
      const blogStore = useBlogStore()

      this.setLoading(true)

      const { data, error } = await useDatoQuery(querySite, {
        variables: {
          locale
        }
      })

      if (error.value) {
        console.error(error.value)
      }

      if (data.value) {
        this._site = data.value._site as Site
        this.allMenuCtas = data.value.allMenuCtas as MenuCtaRecord[]
        this.allMenuItems = data.value.allMenuItems as MenuItemRecord[]
        this.allFooterLinks = data.value.allFooterLinks as FooterLinkRecord[]
        this.allSocialLinks = data.value.allSocialLinks as SocialLinkRecord[]
        this.allUsefulLinks = data.value.allUsefulLinks as UsefulLinkRecord[]
        this.projectsCount = data.value._allProjectsMeta?.count
        this.locationsCount = data.value._allLocationsMeta?.count
        locationStore.pushCategories(data.value.allLocationCategories as LocationCategoryRecord[])
        locationStore.pushCountries(data.value.allCountries as CountryRecord[])
        blogStore.pushCategories(data.value.allCategories as CategoryRecord[])
      }
      this.setLoading(false)
    },
    setLoading(loading: boolean) {
      this.isLoading = loading
    },
    setTheme(theme: 'light' | 'dark') {
      this.theme = theme
    },
    setSimpleFooter(bool: boolean) {
      this.simpleFooter = bool
    },
    setShowFooter(bool: boolean) {
      this.showFooter = bool
    },
    setGradientHeader(gradient: 'cream' | 'black' | 'grey' | 'yellow') {
      this.gradientHeader = gradient
    }
  }
})
